import { BaseCommand } from './baseCommand';
import { SectionType } from '../itemSection';
import store from '@/store';
import Element, { ElementType, SpecyficType } from '../element';
import ChangeColorCommand from './changeColorCommand';
import { IShader } from '../shaders/shader';
import { Pair } from '../pair';
import { Triple } from '../triple';
import { legs } from '../legs';
import ChangeLegsCommand from './changeLegsCommand';
import { handles } from '../handles';
import ChangeHandlesCommand from './changeHandlesCommand';
import { messager } from '../messager';

export class UnifyWoodColorsCommand extends BaseCommand {

    private sectionFuncs: Array<Pair<SectionType, (a: Element) => boolean>> = [
        new Pair(SectionType.BODY_COLOR, (x: Element) => x.type === ElementType.BODY),
        new Pair(SectionType.FRONT_COLOR, (x: Element) => x.type === ElementType.FRONT),
        new Pair(SectionType.SHELF_COLOR, (x: Element) => x.specyficType === SpecyficType.SHELF),
        new Pair(SectionType.WALL_COLOR, (x: Element) => x.type === ElementType.WALL || x.type === ElementType.BEDHEAD),
    ];

    private shaderLegHandles: Array<Triple<string, string, string>> = [
        new Triple('ShaderH3157', 'ShaderNWalec', 'ShaderU06Siro'),
        new Triple('ShaderH3396', 'ShaderNStScieta', 'ShaderU09Siro'),
        new Triple('ShaderH3162', 'ShaderNBox', 'ShaderNull'),
        new Triple('ShaderH3325', 'ShaderNStBox', 'ShaderU01Siro'),
        new Triple('ShaderH3325', 'ShaderNStozek', 'ShaderU01Siro'),
    ];

    private darkOakLegs: Pair<string, string> = new Pair(
        'ShaderNStBox', 'ShaderNStozek',
    );

    private selectedElements: Element[] = [];

    constructor(
        scenePreview: any,
        private sectionType: SectionType,
        private shader: IShader,
        private onlySelected: boolean,
    ) {
        super(scenePreview);
        if (onlySelected) {
            this.selectedElements = scenePreview.selectedElements;
        }
    }

    public execute() {
        if (this.elementSection) {
            this.handleElementSection();
        }
        if (this.sectionType === SectionType.LEGS) {
            this.handleLegsSection();
        }
        if (this.sectionType === SectionType.HANDLES) {
            this.handleHandlesSection();
        }
    }

    private handleLegsSection() {
        this.changeLegs(this.shader.key);
        const shaderLegHandle = this.shaderLegHandles.find((x) =>
            x.y === this.shader.key);
        if (shaderLegHandle) {
            this.changeOtherWoodHandles(shaderLegHandle);
            this.changeFurnitureElements(shaderLegHandle);
        }
    }

    private handleHandlesSection() {
        this.changeHandles(this.shader.key);
        const shaderLegHandle = this.shaderLegHandles.find((x) =>
            x.z === this.shader.key);
        if (shaderLegHandle) {
            this.changeOtherWoodLegs(shaderLegHandle);
            this.changeFurnitureElements(shaderLegHandle);
        }
    }

    private handleElementSection() {
        if (!this.changeColorInSingleElement()) { return; }
        this.changeWoodElementsInSections();
        this.changeLegsAndHandlesForWoodElement();
    }

    private changeColorInSingleElement(): boolean {
        const pair = this.sectionFuncs.find((x) => x.x === this.sectionType);
        if (!pair) { return false; }
        const elements = !this.onlySelected ?
            this.scenePreview.elements
                .filter((x: Element) => pair.y(x)) :
            this.selectedElements;
        if (elements.length === 0) {
            const text = !this.onlySelected ?
                this.scenePreview.$t('noElements').toString() :
                this.scenePreview.$t('noElementsSelected').toString();
            messager.info(text);
            return false;
        }
        const command = new ChangeColorCommand(
            this.scenePreview,
            elements,
            this.shader,
            () => store.commit('incrementTasks'),
            () => store.commit('decrementTasks'),
        );
        this.addCommand(command);
        return true;
    }

    private changeWoodElementsInSections() {
        this.sectionFuncs.forEach((x) => {
            this.changeWoodElementsInSection(x.x, x.y);
        });
    }

    private changeLegsAndHandlesForWoodElement() {
        const shaderLegHandle = this.shaderLegHandles.find((x) =>
            x.x === this.shader.key,
        );
        if (shaderLegHandle) {
            this.changeOtherWoodLegs(shaderLegHandle);
            this.changeOtherWoodHandles(shaderLegHandle);
        }
    }

    private changeLegs(shaderKey: string) {
        const leg = legs.find((x) =>
            x.type === store.getters.product.type &&
            x.shader.key === shaderKey)!;
        if (leg) {
            const command = new ChangeLegsCommand(
                this.scenePreview,
                leg,
            );
            this.addCommand(command);
        }
    }

    private changeOtherWoodLegs(shaderLegHandle: Triple<string, string, string>) {
        const otherWoodLegs = this.shaderLegHandles
            .filter((x) => x !== shaderLegHandle)
            .map((x) => x.y);
        if ( /* WYJĄTEK
            kiedy wybrane mamy już nóżki "noga stożek drewniana", a chcemy
            podmienić na "stelaż skośny drewniany" to nie podmieniamy */
            shaderLegHandle.y === this.darkOakLegs.x &&
            store.getters.shaders.selectedLegs.shader.key === this.darkOakLegs.y
        ) { return; }
        if (otherWoodLegs.includes(store.getters.shaders.selectedLegs.shader.key)) {
            this.changeLegs(shaderLegHandle.y);
        }
    }

    private changeHandles(shaderKey: string) {
        const handle = handles.find((x) =>
            x.shader.key === shaderKey)!;
        if (handle) {
            const command = new ChangeHandlesCommand(
                this.scenePreview,
                handle,
            );
            this.addCommand(command);
        }
    }

    private changeOtherWoodHandles(shaderLegHandle: Triple<string, string, string>) {
        const otherWoodHandles = this.shaderLegHandles
            .filter((x) => x !== shaderLegHandle)
            .map((x) => x.z);
        if (otherWoodHandles.includes(store.getters.shaders.selectedHandle.shader.key) &&
            store.getters.shaders.selectedHandle.shader.key !== 'ShaderNull') {
            this.changeHandles(shaderLegHandle.z);
        }
    }

    private changeFurnitureElements(shaderLegHandle: Triple<string, string, string>) {
        const shaderName = shaderLegHandle.x;
        const furnitureShader = store.getters.shaderByName(shaderName);
        this.sectionFuncs.forEach((x) => {
            this.changeWoodElementsInSection(x.x, x.y, furnitureShader);
        });
    }

    private changeWoodElementsInSection(
        sectionType: SectionType,
        filterFunc: (a: Element) => boolean,
        shader: IShader = this.shader) {
        const elements: Element[] = this.scenePreview.elements
            .filter((x: Element) =>
                filterFunc(x) &&
                store.getters.isWoodShaderByName(x.shaderName));
        if (elements.length === 0) { return; }
        const command = new ChangeColorCommand(
            this.scenePreview,
            elements,
            shader,
            () => store.commit('incrementTasks'),
            () => store.commit('decrementTasks'),
        );
        this.addCommand(command);
    }

    private get elementSection(): boolean {
        return (
            this.sectionType === SectionType.BODY_COLOR ||
            this.sectionType === SectionType.FRONT_COLOR ||
            this.sectionType === SectionType.SHELF_COLOR ||
            this.sectionType === SectionType.WALL_COLOR
        );
    }
}
